import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useParams } from 'react-router-dom';
import ViewHeader from '../components/ViewHeader';
import KpiCard from '../components/KpiCard';
import CategoryBar from '../components/CategoryBar';
import api from '../../../../services/api';

const GrowthTrends = ({ onBack }) => {
    const { clinicId } = useParams();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({
        kpis: [],
        visitTrends: [],
        diagnostics: [],
        trendData: []
    });

    useEffect(() => {
        const fetchGrowthTrends = async () => {
            try {
                // Year parameter can be dynamic if needed, defaulting to 2026 for now as requested
                const response = await api.get(`/clinics/${clinicId}/dashboard/growth?year=2026`);
                if (response.data?.status === 'success') {
                    setData(response.data.data);
                }
            } catch (error) {
                console.error("Error fetching growth trends:", error);
            } finally {
                setLoading(false);
            }
        };

        if (clinicId) {
            fetchGrowthTrends();
        }
    }, [clinicId]);

    if (loading) {
        return <div className="p-4">Loading growth trends...</div>;
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <ViewHeader title="Annual Growth and Trend" subtitle="2026" onBack={onBack} />

            <div className="clinic-dashboard-growth-grid">
                {/* Left column - stacked cards */}
                <div className="clinic-dashboard-growth-left-top clinic-dashboard-section-card" style={{ animationDelay: '0ms' }}>
                    <div className="clinic-dashboard-section-title">Key metrics</div>
                    <div className="clinic-dashboard-kpi-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                        {data.kpis.map((kpi, i) => <KpiCard key={i} {...kpi} />)}
                    </div>
                </div>

                <div className="clinic-dashboard-growth-left-mid clinic-dashboard-section-card" style={{ animationDelay: '80ms' }}>
                    <div className="clinic-dashboard-section-title">Visit trends</div>
                    <div className="clinic-dashboard-catbar-list large" style={{ justifyContent: 'center' }}>
                        {data.visitTrends.map((bar, i) => <CategoryBar key={i} {...bar} />)}
                    </div>
                </div>

                <div className="clinic-dashboard-growth-left-bot clinic-dashboard-section-card" style={{ animationDelay: '160ms' }}>
                    <div className="clinic-dashboard-section-title">Diagnostics</div>
                    <div className="clinic-dashboard-catbar-list large" style={{ justifyContent: 'center' }}>
                        {data.diagnostics.map((bar, i) => <CategoryBar key={i} {...bar} />)}
                    </div>
                </div>

                {/* Right column - tall chart spanning all rows */}
                <div className="clinic-dashboard-growth-right clinic-dashboard-section-card" style={{ animationDelay: '240ms' }}>
                    <div className="clinic-dashboard-section-title">Trailing 12 months trends</div>
                    <div className="clinic-dashboard-chart-wrap" style={{ minHeight: '280px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={data.trendData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis
                                    dataKey="name"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                                />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                                />
                                <Tooltip
                                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                />
                                <Legend
                                    verticalAlign="bottom"
                                    height={36}
                                    iconType="circle"
                                    iconSize={8}
                                    wrapperStyle={{ fontSize: '13px', color: '#4b5563' }}
                                />
                                <Line animationBegin={600} type="monotone" dataKey="revenue" stroke="#0f766e" strokeWidth={2} dot={{ r: 4, fill: '#0f766e' }} activeDot={{ r: 6 }} />
                                <Line animationBegin={600} type="monotone" dataKey="visit" stroke="#f97316" strokeWidth={2} dot={{ r: 4, fill: '#f97316' }} activeDot={{ r: 6 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GrowthTrends;
